import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPreview,
  saveUrlText,
} from '../features/urlTrainerSlice';
import {
  Container,
  Form,
  Button,
  Spinner,
  Row,
  Col,
  Card,
  Alert
} from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import AdminLayout from '../components/AdminLayout';

const UrlTrainerPage = () => {
  const dispatch = useDispatch();
  const {
    text,
    loading,
    visited,
    saved,
    progressLogs,
    error
  } = useSelector((state) => state.urlTrainer);

  const [url, setUrl] = useState('');
  const [skipPatterns, setSkipPatterns] = useState('');
  const [excludeUrls, setExcludeUrls] = useState('');
  const [title, setTitle] = useState('Trained from site');
  const [maxPages, setMaxPages] = useState(5);
  const [deduplicate, setDeduplicate] = useState(true);
  const [removeLowContent, setRemoveLowContent] = useState(true);
  const [minWords, setMinWords] = useState(20);
  const [keywordFilter, setKeywordFilter] = useState('');
  const [showMarkdown, setShowMarkdown] = useState(false);
  const [editableText, setEditableText] = useState('');

  useEffect(() => {
    if (text) setEditableText(text);
  }, [text]);

  const handlePreview = () => {
    dispatch(
      fetchPreview({
        url,
        skip_patterns: skipPatterns.split('\n').filter(Boolean),
        exclude_urls: excludeUrls.split('\n').filter(Boolean),
        max_pages: maxPages,
        deduplicate,
        remove_low_content: removeLowContent,
        min_words: minWords,
        keyword_filter: keywordFilter
      })
    );
  };

  const handleSave = () => {
    dispatch(saveUrlText({ url, text: editableText, title }));
  };

  return (
    <AdminLayout>
      <Container fluid className="pt-4">
        <h4 className="mb-3">Train from Website URL</h4>

        {error && <Alert variant="danger">{error}</Alert>}
        {saved && <Alert variant="success">Training data saved successfully!</Alert>}

        <Form>
          <Row>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Website URL</Form.Label>
                <Form.Control
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Max Pages</Form.Label>
                <Form.Control
                  type="number"
                  value={maxPages}
                  onChange={(e) => setMaxPages(Number(e.target.value))}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={6}>
              <Form.Label>Skip Patterns (Regex)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={skipPatterns}
                onChange={(e) => setSkipPatterns(e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Exclude URLs (one per line)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={excludeUrls}
                onChange={(e) => setExcludeUrls(e.target.value)}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={3}>
              <Form.Label>Min Words</Form.Label>
              <Form.Control
                type="number"
                value={minWords}
                onChange={(e) => setMinWords(Number(e.target.value))}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Keyword Filter (comma-separated)</Form.Label>
              <Form.Control
                value={keywordFilter}
                onChange={(e) => setKeywordFilter(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={3}>
              <Form.Check
                label="Remove Duplicates"
                checked={deduplicate}
                onChange={(e) => setDeduplicate(e.target.checked)}
              />
            </Col>
            <Col md={3}>
              <Form.Check
                label="Remove Low Content"
                checked={removeLowContent}
                onChange={(e) => setRemoveLowContent(e.target.checked)}
              />
            </Col>
          </Row>

          <div className="mt-4">
            <Button onClick={handlePreview} disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Preview Content'}
            </Button>
            {text && (
              <Button
                variant="success"
                className="ms-2"
                onClick={handleSave}
                disabled={!editableText}
              >
                Save Training
              </Button>
            )}
            {text && (
              <Button
                variant="outline-secondary"
                className="ms-2"
                onClick={() => setShowMarkdown(!showMarkdown)}
              >
                {showMarkdown ? 'Show Raw' : 'Preview Markdown'}
              </Button>
            )}
          </div>
        </Form>

        {loading && progressLogs?.length > 0 && (
          <Card className="mt-4 p-3 bg-light small">
            <h6 className="text-muted">Progress Log</h6>
            <pre className="mb-0" style={{ maxHeight: 200, overflowY: 'auto' }}>
              {progressLogs.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </pre>
          </Card>
        )}

        {text && (
          <Card className="mt-4 p-3">
            <h6 className="mb-2">Editable Training Content</h6>
            {showMarkdown ? (
              <div className="markdown-body">
                <ReactMarkdown
                  children={editableText}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                />
              </div>
            ) : (
              <Form.Control
                as="textarea"
                rows={20}
                value={editableText}
                onChange={(e) => setEditableText(e.target.value)}
              />
            )}
          </Card>
        )}
      </Container>
    </AdminLayout>
  );
};

export default UrlTrainerPage;
